/**
 * How it works › Admin guide (21/10/2026 — user: "the PDF … into an animated
 * and editable professional way for admins … I want to see everything and
 * have full control"): the seven sections of the engine guide as cards, one
 * open at a time, each picture animating in, every engine number read live,
 * and each section ending with the settings it explains.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ShieldCheck } from 'lucide-react';

import { Button, Skeleton, StepCard, StepCardGroup } from '@/components/ui';

import { GUIDE_SECTIONS } from './guide-sections';
import { SectionSettings } from './settings/SectionSettings';
import { useEngineSettings } from './use-engine-settings';

const Intro: FC = () => (
  <p className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
    <span>
      How the engine runs every country, with the settings behind each part. Only admins see this; a saved setting applies to every country within a minute.
    </span>
  </p>
);

export const AdminGuide: FC = () => {
  const settings = useEngineSettings();
  if (settings.isLoading) return <Skeleton count={7} className="h-12" />;
  if (settings.isError || !settings.data)
    return (
      <p className="flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
        The engine settings could not be read.
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => void settings.refetch()}>Retry</Button>
      </p>
    );
  const s = settings.data;
  return (
    <div className="flex flex-col gap-3" data-testid="admin-guide">
      <Intro />
      <StepCardGroup defaultOpen={1}>
        {GUIDE_SECTIONS.map(({ n, Icon, title, summary, Body, keys }) => (
          <StepCard key={n} index={n} icon={Icon} title={title} summary={summary(s)}>
            <div className="flex flex-col gap-4">
              <Body s={s} />
              {keys.length > 0 ? <SectionSettings keys={keys} saved={s} /> : null}
            </div>
          </StepCard>
        ))}
      </StepCardGroup>
    </div>
  );
};
